import { useEffect, useRef, useState } from "react";
import { Button, PocketRow, PocketSection } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { DisclosurePanel } from "@/components/ui/disclosure-panel";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import {
  SHORTCUT_CONTEXT_KEY,
  SHORTCUT_LABEL_KEY,
  settingsShortcutsMessages,
} from "@/i18n/messages/settings-shortcuts";
import { cn } from "@/lib/cn";
import { WHATSAPP_COMMUNITY_URL } from "@/lib/community-url";
import { openExternalUrl } from "@/lib/open-external-url";
import {
  emitDesktopAction,
  type DesktopAction,
} from "@/lib/desktop-action-events";
import { isTauri } from "@/lib/is-tauri";
import {
  RECORD_SCREEN_BINDING,
  type ClaudeShortcutClash,
  type ClashTarget,
} from "@/lib/claude-app-shortcuts";
import {
  SHORTCUT_REGISTRY,
  describeShortcut,
  getDefaultBinding,
  shortcutKeyNames,
  shortcutPlatform,
  type ShortcutDefinition,
  type ShortcutId,
} from "@/lib/shortcut-registry";
import { shortcutNeedsKey, useShortcutStore } from "@/stores/shortcut-store";
import {
  useClaudeAppShortcutRefresh,
  useClaudeShortcutClashes,
} from "@/stores/claude-app-shortcut-store";
import { ClaudeShortcutClashNotice } from "./claude-shortcut-clash-notice";
import { useSettingsNav } from "./settings-navigation";
import { ShortcutKeyboardCrop } from "./shortcut-keyboard-crop";
import { useShortcutRecorder } from "./use-shortcut-recorder";

/** Rows that run their action on click, exactly as the shortcut does. */
const ROW_ACTIONS: Partial<Record<ShortcutId, DesktopAction>> = {
  "shortcut.toggle_workspace": "toggle-workspace",
  "shortcut.input.open": "text-chat",
  "shortcut.voice.toggle": "voice-chat",
};

function ShortcutRow({
  shortcut,
  clash,
}: {
  shortcut: ShortcutDefinition;
  clash?: ClaudeShortcutClash;
}) {
  const nav = useSettingsNav();
  const t = useT(settingsShortcutsMessages);
  const label = t(SHORTCUT_LABEL_KEY[shortcut.id]);
  const contextKey = SHORTCUT_CONTEXT_KEY[shortcut.id];
  const override = useShortcutStore((state) => state.overrides[shortcut.id]);
  const binding = override ?? getDefaultBinding(shortcut.id);
  const action = isTauri() ? ROW_ACTIONS[shortcut.id] : undefined;
  const editShortcut = () =>
    nav.push({
      page: "preferences-shortcut-edit",
      args: { shortcutId: shortcut.id, title: label },
    });
  return (
    <PocketRow
      testId={shortcut.testId}
      onSelect={
        action
          ? () => {
              // The input bar opens at the pointer, which is on this pocket.
              if (action === "text-chat") nav.close();
              void emitDesktopAction(action);
            }
          : undefined
      }
      title={<span className="block whitespace-normal">{label}</span>}
      secondLine={
        shortcut.id === "shortcut.draw" && contextKey ? (
          <span className="block whitespace-normal">{t(contextKey)}</span>
        ) : override ? (
          t("customShortcut")
        ) : undefined
      }
      trailing={
        <Button
          variant="ghost"
          data-testid={`edit-${shortcut.id}`}
          aria-label={t("changeShortcut", { name: label })}
          className="h-auto w-[120px] flex-col gap-1 rounded-control-sm px-1 py-1 text-center focus-visible:ring-2 focus-visible:ring-accent"
          onClick={editShortcut}
        >
          <ShortcutKeyboardCrop binding={binding} />
          <span className="whitespace-normal text-appkit-mini leading-[1.3] text-muted">
            {describeShortcut(binding)}
          </span>
        </Button>
      }
      below={
        clash ? (
          <ClaudeShortcutClashNotice clash={clash} onPickKey={editShortcut} />
        ) : undefined
      }
    />
  );
}

export function PreferencesShortcutsPage() {
  const nav = useSettingsNav();
  const t = useT(settingsShortcutsMessages);
  const expanded = nav.stack[nav.stack.length - 1]?.args?.expanded === "true";
  const moreRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (nav.stack[nav.stack.length - 1]?.args?.expanded !== undefined)
      moreRef.current?.focus({ preventScroll: true });
  }, [nav.stack]);
  const [operationError, setOperationError] = useState<string | null>(null);
  const overrides = useShortcutStore((state) => state.overrides);
  const loading = useShortcutStore((state) => state.loading);
  const error = useShortcutStore((state) => state.error);
  const hydrate = useShortcutStore((state) => state.hydrate);
  const resetAll = useShortcutStore((state) => state.resetAll);
  useEffect(() => {
    void hydrate();
  }, [hydrate]);
  useClaudeAppShortcutRefresh();
  const clashes = useClaudeShortcutClashes();
  const clashFor = (target: ClashTarget) =>
    clashes.find((clash) => clash.target === target);
  const recordScreenClash = clashFor("record-screen");
  return (
    <div
      data-testid="settings-page-preferences-shortcuts"
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex shrink-0 items-center justify-between px-2 pb-2 text-appkit-small text-muted">
        <p>{t(isTauri() ? "hintDesktop" : "hintWeb")}</p>
        <Button
          variant="ghost"
          size="iconSm"
          className="text-muted"
          aria-label={t("helpCommunity")}
          title={t("helpCommunity")}
          data-testid="shortcuts-help-community"
          onClick={async () => {
            setOperationError(null);
            try {
              await openExternalUrl(WHATSAPP_COMMUNITY_URL);
            } catch {
              setOperationError(t("linkFailed"));
            }
          }}
        >
          <Icons.HelpCircle className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto">
        <PocketSection>
          {SHORTCUT_REGISTRY.filter((shortcut) => shortcut.primary).map(
            (shortcut) => (
              <ShortcutRow
                key={shortcut.id}
                shortcut={shortcut}
                clash={clashFor(shortcut.id)}
              />
            ),
          )}
          <PocketRow
            testId="shortcut-record-screen"
            title={
              <span className="block whitespace-normal">
                {t("recordScreen")}
              </span>
            }
            secondLine={
              <span className="block whitespace-normal">
                {t("recordScreenDetail")}
              </span>
            }
            trailing={
              <div className="flex w-[120px] flex-col items-center gap-1 px-1 py-1 text-center">
                <ShortcutKeyboardCrop binding={RECORD_SCREEN_BINDING} />
                <span className="whitespace-normal text-appkit-mini leading-[1.3] text-muted">
                  {t("holdCapsLock")}
                </span>
              </div>
            }
            below={
              recordScreenClash ? (
                <ClaudeShortcutClashNotice clash={recordScreenClash} />
              ) : undefined
            }
          />
        </PocketSection>
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 w-full focus-visible:ring-2 focus-visible:ring-accent"
          ref={moreRef}
          data-testid="more-shortcuts"
          aria-expanded={expanded}
          aria-controls="additional-shortcuts"
          onClick={() =>
            nav.open({
              page: "preferences-shortcuts",
              args: { expanded: String(!expanded) },
            })
          }
        >
          {t(expanded ? "fewerShortcuts" : "moreShortcuts")}
          <Icons.ChevronDown
            className="denker-disclosure-chevron h-3 w-3"
            data-open={expanded}
            aria-hidden="true"
          />
        </Button>
        <DisclosurePanel
          id="additional-shortcuts"
          open={expanded}
          returnFocusRef={moreRef}
        >
          {(["Input", "Workspace"] as const).map((group) => (
            <PocketSection
              key={group}
              title={t(group === "Input" ? "groupInput" : "groupWorkspace")}
            >
              {SHORTCUT_REGISTRY.filter(
                (shortcut) => shortcut.group === group && !shortcut.primary,
              ).map((shortcut) => (
                <ShortcutRow
                  key={shortcut.id}
                  shortcut={shortcut}
                  clash={clashFor(shortcut.id)}
                />
              ))}
            </PocketSection>
          ))}
        </DisclosurePanel>
      </div>
      {operationError || error ? (
        <p role="alert" className="px-2 pt-2 text-appkit-small text-danger">
          {operationError ?? error}
        </p>
      ) : null}
      <div className="mt-3 flex shrink-0 items-center justify-between px-2 py-1 text-appkit-small text-muted">
        <span className="min-w-0">
          {t("platformShortcuts", {
            platform: shortcutPlatform() === "macos" ? "macOS" : "Windows",
          })}
        </span>
        <Button
          variant="ghost"
          size="sm"
          data-testid="shortcuts-reset-all"
          disabled={loading || (!Object.keys(overrides).length && !error)}
          onClick={async () => {
            setOperationError(null);
            try {
              await resetAll();
            } catch (reason) {
              setOperationError(
                reason instanceof Error ? reason.message : String(reason),
              );
            }
          }}
        >
          {t("resetAll")}
        </Button>
      </div>
    </div>
  );
}

export function ShortcutEditPage({ shortcutId }: { shortcutId: string }) {
  const t = useT(settingsShortcutsMessages);
  const shortcut = SHORTCUT_REGISTRY.find((item) => item.id === shortcutId);
  if (!shortcut)
    return (
      <p className="px-2 text-appkit-small text-muted">{t("unavailable")}</p>
    );
  return <ShortcutEditor shortcut={shortcut} />;
}

function ShortcutEditor({ shortcut }: { shortcut: ShortcutDefinition }) {
  const nav = useSettingsNav();
  const t = useT(settingsShortcutsMessages);
  const tCommon = useT(commonMessages);
  const contextKey = SHORTCUT_CONTEXT_KEY[shortcut.id];
  const defaults = getDefaultBinding(shortcut.id);
  const recorder = useShortcutRecorder(defaults, shortcut.scope === "global");
  const override = useShortcutStore((state) => state.overrides[shortcut.id]);
  const needsKey = useShortcutStore((state) =>
    shortcutNeedsKey(state, shortcut.id),
  );
  const save = useShortcutStore((state) => state.save);
  const reset = useShortcutStore((state) => state.reset);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function persist(id: ShortcutId, resetting: boolean) {
    setBusy(true);
    setError(null);
    recorder.stop();
    try {
      if (resetting) await reset(id);
      else if (recorder.candidate) await save(id, recorder.candidate);
      nav.back();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="settings-page-preferences-shortcut-edit" className="px-2">
      <PocketSection title={t("defaultShortcut")}>
        <div className="flex min-h-[102px] flex-col items-center justify-center gap-2 rounded-[16px] bg-[var(--color-surface-selected)] py-3">
          <ShortcutKeyboardCrop binding={defaults} large />
          <span className="text-appkit-small text-primary">
            {describeShortcut(defaults)}
          </span>
        </div>
        {contextKey ? (
          <p className="mt-2 text-appkit-small text-muted">{t(contextKey)}</p>
        ) : null}
      </PocketSection>
      <PocketSection title={t("yourShortcut")}>
        <Button
          ref={recorder.captureRef}
          variant="secondary"
          data-testid="shortcut-capture"
          disabled={busy}
          aria-busy={recorder.starting}
          aria-live="polite"
          aria-label={t("recordNew")}
          className={cn(
            "min-h-[112px] h-auto w-full flex-col gap-2 whitespace-normal rounded-[16px] py-4 focus-visible:ring-2 focus-visible:ring-accent",
            recorder.recording && "ring-1 ring-accent",
          )}
          onClick={() => void recorder.start()}
        >
          {recorder.candidate ? (
            <>
              <ShortcutKeyboardCrop binding={recorder.candidate} />
              <span>{describeShortcut(recorder.candidate)}</span>
            </>
          ) : (
            <>
              <span>{t(recorder.recording ? "settingNew" : "clickToSet")}</span>
              <span className="text-appkit-small text-muted">
                {recorder.pressedLabel.length
                  ? shortcutKeyNames(recorder.pressedLabel)
                  : t(recorder.recording ? "pressKeys" : "thenPress")}
              </span>
            </>
          )}
        </Button>
        <p className="mt-2 text-appkit-small text-muted">
          {override
            ? t("currently", { shortcut: describeShortcut(override) })
            : t(needsKey ? "defaultInUse" : "usingDefault")}
        </p>
        {error || recorder.error ? (
          <p role="alert" className="mt-2 text-appkit-small text-danger">
            {error ?? recorder.error}
          </p>
        ) : null}
        <div className="mt-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            data-testid="shortcut-reset"
            disabled={busy || (!override && !recorder.candidate)}
            onClick={() => void persist(shortcut.id, true)}
          >
            {t("resetToDefault")}
          </Button>
          <Button
            size="sm"
            data-testid="shortcut-save"
            disabled={busy || recorder.starting || !recorder.candidate}
            onClick={() => void persist(shortcut.id, false)}
          >
            {tCommon("save")}
          </Button>
        </div>
      </PocketSection>
    </div>
  );
}
